import { gql } from "lib/apolloClient";
import {
  staffFragment,
  studentFragment,
  genericFolderFragment,
  toolResponseFragment,
  meetingFragment,
  taggedPracticesFragment,
  genericTagFragment,
  ibPypElementRatingFragment,
  studentsAlertFragment,
  pypElementFragment,
  plannerElementNodeFragment,
  subjectFragment,
  fieldScoreFragment,
  subjectGroupFragment,
  similarityFragment,
} from "modules/CommonFragments";
import { rubricFragment } from "Rubric/modules/RubricFragments";
import { checklistFragment } from "Checklist/modules/ChecklistFragments";
import { singlePointRubricFragment } from "SinglePointRubric/modules/SinglePointRubricFragments";
import { organizationResourceFragment } from "OrganizationResources/modules/OrganizationResourcesFragments";
import { assessmentToolFragment } from "modules/AssessmentTool/AssessmentToolFragment";
import { questionSubmissionConnectionFragments } from "Worksheet/modules/WorksheetFragments";
import { standardRubricFragment } from "AppComponents/StandardRubric/modules/StandardRubricFragments";
import { gradeScaleToolFragment } from "AppComponents/AssessmentGradeScaleTool/modules/AssessmentGradeScaleToolFragments";

export const elementRemarkFragment = {
  elementRemark: gql`
    fragment elementRemarkItem on IbPypElementRemark {
      id
      message
      creatorType
      ibPYPElementId
      isDeleted
      ibPYPElementType
    }
  `,
};

export const entityEventTrackFragment = {
  entityEventTrack: gql`
    fragment entityEventTrackItem on EntityEventTrack {
      id
      eventType
      eventTime
      duration
      reason
    }
  `,
};

export const studentDetailsFragment = {
  basicStudentDetails: gql`
    fragment studentItem on Student {
      id
      firstName
      middleName
      lastName
      pronouns
      preferredName
      prefix
      suffix
      profileImage
      email
      type
      isDevUser
      isArchived
      isArchivedV2(filters: $userArchivedFilters)
      sourceId
      type
      alertTypes {
        ...alertTypeFields
      }
    }
    ${studentsAlertFragment}
  `,
};

export const attachmentFragment = {
  attachment: gql`
    fragment attachmentItem on Attachment {
      id
      name
      type
      mimeType
      url
      signedUrl
      thumbUrl
      title
      metadata
      streamUrl
      parentType
      isRead
      similarityReport {
        ...similarityItem
      }
      childAttachments {
        id
        type
      }
      createdBy {
        id
        type
      }
      lockingState {
        state
        scheduledAt
      }
      questionAttachment {
        ... on QuestionSubmission {
          id
          questionScore
          score {
            id
            value
          }
        }
        ... on Question {
          id
          questionScore
        }
      }
    }
    ${similarityFragment.similarityItem}
  `,
};

export const attachmentGroupsFragment = {
  attachmentGroup: gql`
    fragment attachmentGroupItem on AttachmentGroup {
      id
      metadata
      attachments {
        ...attachmentItem
      }
      studentTemplate {
        id
      }
    }
    ${attachmentFragment.attachment}
  `,
};

export const studentAssigmentSubmissionFragment = {
  studentAssigmentSubmission: gql`
    fragment studentAssigmentSubmissionItem on StudentAssignmentSubmission {
      id
      response
      questionSubmissions {
        totalCount
        totalQuestionCount
        totalQuestionAttempted
      }
      attachmentGroups {
        ...attachmentGroupItem
      }
      attachments {
        ...attachmentItem
      }
      status
      statusV2
      submittedAt
      returnedAt
      isDraftSubmission
      endTime
      isPaused
      systemStatus
      overrideStatusUpdatedBy {
        id
        type
      }
      overrideStatusUpdatedAt
    }
    ${attachmentGroupsFragment.attachmentGroup}
    ${attachmentFragment.attachment}
  `,
  studentAssigmentSubmissionV2: gql`
    fragment studentAssigmentSubmissionItem on StudentAssignmentSubmission {
      id
      response
      questionSubmissions {
        ...questionSubmissionConnectionItem
      }
      attachmentGroups {
        ...attachmentGroupItem
      }
      attachments {
        ...attachmentItem
      }
      status
      statusV2
      submittedAt
      returnedAt
      isDraftSubmission
      systemStatus
      overrideStatusUpdatedBy {
        id
        type
      }
      overrideStatusUpdatedAt
    }
    ${attachmentGroupsFragment.attachmentGroup}
    ${attachmentFragment.attachment}
    ${questionSubmissionConnectionFragments.questionSubmissionConnection}
  `,
  studentAssigmentSubmissionWithEvaluation: gql`
    fragment studentAssigmentSubmissionItem on StudentAssignmentSubmission {
      id
      response
      questionSubmissions {
        totalCount
        totalQuestionCount
        totalQuestionAttempted
      }
      attachmentGroups {
        ...attachmentGroupItem
      }
      attachments {
        ...attachmentItem
      }
      status
      statusV2
      submittedAt
      returnedAt
      isPaused
      endTime
      isLockScreenEnabled
      isDraftSubmission
      systemStatus
      overrideStatusUpdatedBy {
        id
        type
      }
      overrideStatusUpdatedAt
      evaluations {
        toolResponses(filters: { userFilter: $userFilter }) {
          id
          toolId
          toolType
          toolResponses {
            ...toolResponseItem
          }
        }
        ibPYPElementRatings {
          ...ratingItem
        }
        elementRemarks {
          ...elementRemarkItem
        }
        isEvaluated
        isSharedWithStudent
        evaluatedAt
      }
    }
    ${attachmentGroupsFragment.attachmentGroup}
    ${attachmentFragment.attachment}
    ${elementRemarkFragment.elementRemark}
    ${toolResponseFragment.toolResponse}
    ${ibPypElementRatingFragment.studentElementRating}
  `,
};

export const studentAssignmentFragment = {
  studentAssignmentDetails: gql`
    fragment studentAssignmentItem on StudentAssignment {
      id
      student {
        ...studentItem
      }
      state
      isSubmitted
      aiUsageMetrics {
        id
        type
        userFeedback {
          myVote
          feedback
        }
      }
      aiConversation {
        id
        metadata
        messages {
          totalCount
        }
        analysisToolEvaluation(filters: { userFilter: ALL }) {
          id
          toolResponses {
            ...toolResponseItem
          }
        }
      }
      aiConversations(orderBy: CREATED_AT, orderByDirection: ASC) {
        edges {
          node {
            id
            entityType
            entitySubType
            messages {
              edges {
                node {
                  id
                  authorType
                }
              }
              totalCount
            }
            analysisToolEvaluation(filters: { userFilter: ALL }) {
              id
              toolResponses {
                id
              }
            }
          }
        }
      }
      conversation {
        id
        unreadMessageCount
        unreadMessageCounts {
          itemTypeV2
          itemType
          count
        }
      }
      submission(showOnlySubmitted: $showOnlySubmitted) {
        ...studentAssigmentSubmissionItem
      }
      toolResponses: toolResponsesV2(
        filters: { userFilter: $userFilter, isFetchDeletedAiRatings: true }
      ) {
        id
        toolId
        toolType
        toolResponses {
          ...toolResponseItem
        }
      }
      ibPYPElementRatings(filters: { isFetchDeletedAiRatings: true }) {
        ...ratingItem
      }
      elementRemarks(filters: { isFetchDeletedAiRatings: true }) {
        ...elementRemarkItem
      }
      isEvaluated
      evaluatedAt
      isEvaluationApplicable
      isSharedWithStudent
      status
      aiEvaluationJob(filters: { isFetchLatestJob: true }) {
        id
        status
        jobStartExecutionTime
        metadata
      }
      isSharedWithStudent
      assignment {
        id
        isSimilarityCheckEnabled
        showStudentResponse
        course {
          id
          grade {
            id
          }
          grades {
            id
            name
          }
          primaryGrade {
            id
            name
          }
          subjects {
            id
            name
            type
          }
          subjectGroups {
            id
            type
          }
        }
        curriculumProgram {
          id
          type
        }
        closeSubmissionDate
        content {
          id
        }
      }
      post {
        id
        publishedAt
        state
      }
      events(filters: $entityEventsFilter) {
        ...entityEventTrackItem
      }
      lockingState {
        state
      }
    }
    ${elementRemarkFragment.elementRemark}
    ${studentAssigmentSubmissionFragment.studentAssigmentSubmission}
    ${studentDetailsFragment.basicStudentDetails}
    ${toolResponseFragment.toolResponse}
    ${ibPypElementRatingFragment.studentElementRating}
    ${entityEventTrackFragment.entityEventTrack}
  `,
  studentAssignmentDetailsV2: gql`
    fragment studentAssignmentItem on StudentAssignment {
      id
      student {
        ...studentItem
      }
      state
      isSubmitted
      aiConversation {
        id
      }
      aiConversations(orderBy: CREATED_AT, orderByDirection: ASC) {
        edges {
          node {
            id
            entityType
            entitySubType
            messages {
              edges {
                node {
                  id
                  authorType
                }
              }
              totalCount
            }
          }
        }
      }
      conversation {
        id
        unreadMessageCount
        unreadMessageCounts {
          itemTypeV2
          itemType
          count
        }
      }
      submission(showOnlySubmitted: $showOnlySubmitted) {
        ...studentAssigmentSubmissionItem
      }
      toolResponses: toolResponsesV2(filters: { userFilter: $userFilter }) {
        id
        toolId
        toolType
        toolResponses {
          ...toolResponseItem
        }
      }
      ibPYPElementRatings(filters: { isFetchDeletedAiRatings: true }) {
        ...ratingItem
      }
      elementRemarks(filters: { isFetchDeletedAiRatings: true }) {
        ...elementRemarkItem
      }
      isEvaluated
      evaluatedAt
      isEvaluationApplicable
      isSharedWithStudent
      status
      isSharedWithStudent
      assignment {
        id
        isSimilarityCheckEnabled
        course {
          id
          grade {
            id
          }
          grades {
            id
            name
          }
          primaryGrade {
            id
            name
          }
          subjects {
            id
            name
            type
          }
          subjectGroups {
            id
            type
          }
        }
        curriculumProgram {
          id
          type
        }
      }
      post {
        id
        publishedAt
        state
      }
      events(filters: $entityEventsFilter) {
        ...entityEventTrackItem
      }
      lockingState {
        state
      }
    }
    ${elementRemarkFragment.elementRemark}
    ${studentAssigmentSubmissionFragment.studentAssigmentSubmissionV2}
    ${studentDetailsFragment.basicStudentDetails}
    ${toolResponseFragment.toolResponse}
    ${ibPypElementRatingFragment.studentElementRating}
    ${entityEventTrackFragment.entityEventTrack}
  `,
  allSubmissionFragment: gql`
    fragment studentAssignmentItem on StudentAssignment {
      id
      student {
        ...studentItem
      }
      submission(showOnlySubmitted: false) {
        ...studentAssigmentSubmissionItem
      }
      ibPYPElementRatings {
        id
        value
        ibPYPElementId
        ibPYPElementType
        academicCriteriaSet {
          id
          academicCriteriaValues {
            id
          }
        }
        academicCriteriaValue {
          id
          label
        }
      }
      isEvaluated
      isSubmitted
      isEvaluationApplicable
      isSharedWithStudent
      status
      post {
        id
      }
      conversation {
        id
        unreadMessageCount
      }
      events(filters: $entityEventsFilter) {
        ...entityEventTrackItem
      }
      lockingState {
        state
      }
    }
    ${studentAssigmentSubmissionFragment.studentAssigmentSubmission}
    ${studentFragment.basicStudentDetails}
    ${entityEventTrackFragment.entityEventTrack}
  `,
  studentAssignmentBasicDetails: gql`
    fragment studentAssignmentItem on StudentAssignment {
      id
      status
      isSubmitted
      isEvaluated
      state
      conversation {
        id
        unreadMessageCount
      }
      submission {
        id
        status
        statusV2
      }
      assignment {
        id
        label
        contentType
        isFixedStartTime
        isStudentSubmissionEnabled
        isTeacherEvaluationEnabled
        deadline
        closeSubmissionDate
        visibility
        state {
          state
          publishedAt
        }
        assignedStudents {
          id
          state
        }
        course {
          id
          title
          learningCourse {
            id
            title
            academicCourse {
              id
              label
            }
          }
        }
        content {
          ... on Assessment {
            id
            assessmentType {
              id
              value
            }
            title {
              id
              value
            }
            image {
              id
              value
            }
          }
          ... on AssignmentResource {
            id
            label
          }
        }
        curriculumProgram {
          id
          curriculum {
            id
            type
          }
        }
        classDiscussion {
          id
          unreadMessageCount
        }
      }
      lockingState {
        state
      }
      isEvaluationApplicable
    }
  `,
  studentAssignment: gql`
    fragment studentAssignmentItem on StudentAssignment {
      id
      state
      status
      isSubmitted
      isEvaluationApplicable
      student {
        ...studentItem
      }
      submissions {
        edges {
          ...studentAssigmentSubmissionItem
        }
      }
      lockingState {
        state
        updatedAt
      }
      assignment {
        id
        isSimilarityCheckEnabled
        showStudentResponse
        course {
          id
          grade {
            id
          }
          grades {
            id
            name
          }
          primaryGrade {
            id
            name
          }
          subjects {
            id
            name
            type
          }
          subjectGroups {
            id
            type
          }
        }
        curriculumProgram {
          id
          type
        }
        closeSubmissionDate
        content {
          id
        }
      }
      aiConversations(orderBy: CREATED_AT, orderByDirection: ASC) {
        edges {
          node {
            id
            entityType
            entitySubType
            messages {
              edges {
                node {
                  id
                  authorType
                }
              }
              totalCount
            }
            analysisToolEvaluation(filters: { userFilter: ALL }) {
              id
              toolResponses {
                id
              }
            }
          }
        }
      }
      conversation {
        id
        unreadMessageCount
        unreadMessageCounts {
          itemTypeV2
          itemType
          count
        }
      }
      aiEvaluationJob(filters: { isFetchLatestJob: true }) {
        id
        status
        metadata
      }
      post {
        id
        publishedAt
        state
      }
      events(filters: $entityEventsFilter) {
        ...entityEventTrackItem
      }
    }
    ${studentAssigmentSubmissionFragment.studentAssigmentSubmissionWithEvaluation}
    ${studentDetailsFragment.basicStudentDetails}
    ${entityEventTrackFragment.entityEventTrack}
  `,
};

export const assignedStudentsFragment = {
  assignedStudents: gql`
    fragment assignedStudents on Assignment {
      assignedStudents(studentIds: $studentIds) {
        id
        student {
          ...studentItem
          ... on Student {
            id
            alertTypes {
              ...alertTypeFields
            }
          }
        }
        conversation {
          id
          unreadMessageCount
          unreadMessageCounts(filters: $unreadMessageCountFilters) {
            itemTypeV2
            itemType
            count
          }
          lastMessage {
            id
            isRead
            createdAt
          }
        }
        submission {
          id
          status
          statusV2
        }
        state
        status
        isEvaluated
        isSharedWithStudent
        isEvaluationApplicable
        isSubmitted
        lockingState {
          state
        }
      }
    }
    ${studentsAlertFragment}
  `,
};

export const assignmentSummaryCountsFragment = {
  assignmentSummaryCounts: gql`
    fragment assignmentSummaryCounts on Assignment {
      summaryCounts {
        assigned
        evaluated
        turnedIn
        evaluationShared
        draftSubmissions
        unreadMessagesCount(studentIds: $studentIds)
        newSubmissionsCount
        lockedSubmissions
        unlockedSubmissions
        evaluationApplicable
      }
    }
  `,
};

export const assignmentMessagesSummaryFragment = {
  assignmentMessagesSummary: gql`
    fragment assignmentMessagesSummary on Assignment {
      messagesSummary {
        unreadMessagesCount(filters: { studentIds: $studentIds })
        unreadMessages(first: 1) {
          id
          label
          attachments {
            id
            name
            title
            mimeType
            type
          }
          createdBy {
            id
            firstName
            lastName
            middleName
            type

            ... on Staff {
              preferredName
              prefix
              suffix
              pronouns
            }

            ... on Student {
              pronouns
              preferredName
              suffix
              prefix
              profileImage
            }
          }
          createdAt
          parentEntity {
            id
            ... on Conversation {
              id
              parentType
              parentEntity {
                id
              }
            }
          }
        }
      }
    }
  `,
};

export const commonResolvedFieldValueFragment = gql`
  fragment commonResolvedFieldValueItem on ResolvedFieldValue {
    id
    type
    ... on ResolvedFieldBenchmarkSet {
      id
      benchmarks {
        ...benchmarkItem
        gradingType
      }
    }
    ... on ResolvedFieldConceptSet {
      id
      concepts {
        ...conceptItem
      }
    }
    ... on ResolvedFieldActionSet {
      id
      actions {
        ...actionItem
      }
    }
    ... on ResolvedFieldLPSet {
      id
      learnerProfiles {
        ...lpItem
      }
    }
    ... on ResolvedFieldThemeSet {
      id
      themes {
        ...themeItem
      }
    }
    ... on ResolvedFieldSubjectSet {
      id
      subjects {
        ...subjectItem
        subjectGroup {
          id
          name
          constants
          globalSubjectGroup {
            id
            name
            constants
          }
        }
      }
    }
    ... on ResolvedFieldATLSet {
      id
      atls {
        ...atlItem
      }
    }
    ... on ResolvedFieldRelatedConceptSet {
      id
      relatedConcepts {
        ...relatedConceptItem
      }
    }
    ... on ResolvedFieldVoiceInstruction {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldVideoAttachment {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldStudentTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldLtiTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldOpeningMessageResource {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldTags {
      id
      tags {
        id
        label
      }
    }
    ... on ResolvedFieldGenericTags {
      id
      genericTags {
        id
        label
        __typename
      }
      __typename
    }
    ... on ResolvedFieldPlannerElementSet {
      id
      type
      nodes {
        ...plannerElementNodeItem
        gradingType
        standardsWrapper {
          standardSet {
            id
            title
          }
          properties {
            isGradedStandard
          }
        }
        plannerElementNodeWrapper {
          properties {
            isGradedStandard
          }
        }
        dataSetNodeWrapper {
          properties {
            isGraded
          }
        }
      }
    }
    ... on ResolvedFieldSubjectGroup {
      id
      type
      subjectGroups {
        ...subjectGroupWithGlobalSubjectGroupItem
      }
    }
    ... on ResolvedFieldScore {
      ...fieldScoreItem
    }
    ... on ResolvedFieldObjectiveLevel {
      id
      nodes {
        id
        label
        associatedParents {
          id
        }
      }
    }
    ... on ResolvedFieldAttachments {
      id
      type
      attachments {
        ...attachmentItem
      }
    }
  }
`;

export const unitPlanFragment = {
  unitPlanBasicDetails: gql`
    fragment unitPlanBasicDetailsItem on UnitPlan {
      id
      templateId
      grades
      title {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ... on ResolvedFieldVideoAttachment {
            id
            type
            attachment {
              id
              type
              url
              thumbUrl
            }
          }
        }
      }
      allFields {
        id
        uid
        value
        resolvedMinimalTree {
          ...commonResolvedFieldValueItem
        }
      }
      subjects {
        id
        value
      }
      resolvedGrades {
        id
        name
      }
      courses {
        id
      }
      organization {
        id
      }
    }
    ${subjectGroupFragment.subjectGroupBasicDetails}
    ${subjectGroupFragment.subjectGroupWithGlobalSubjectGroup}
    ${plannerElementNodeFragment.plannerElementNode}
    ${pypElementFragment.benchmark}
    ${pypElementFragment.concept}
    ${pypElementFragment.action}
    ${pypElementFragment.lp}
    ${pypElementFragment.theme}
    ${pypElementFragment.atl}
    ${pypElementFragment.relatedConcept}
    ${subjectFragment.subjectBenchmarkDetails}
    ${attachmentFragment.attachment}
    ${attachmentGroupsFragment.attachmentGroup}
    ${fieldScoreFragment.fieldScore}
    ${commonResolvedFieldValueFragment}
  `,
};

export const resolvedFieldValueFragment = {
  resolvedMinimalTree: gql`
    fragment resolvedMinimalTreeItem on ResolvedFieldValue {
      ... on ResolvedFieldUnitPlan {
        id
        type
        unitPlan {
          ...unitPlanBasicDetailsItem
        }
      }
      ...commonResolvedFieldValueItem
    }

    ${subjectGroupFragment.subjectGroupBasicDetails}
    ${plannerElementNodeFragment.plannerElementNode}
    ${pypElementFragment.benchmark}
    ${pypElementFragment.concept}
    ${pypElementFragment.action}
    ${pypElementFragment.lp}
    ${pypElementFragment.theme}
    ${pypElementFragment.atl}
    ${pypElementFragment.relatedConcept}
    ${subjectFragment.subjectBenchmarkDetails}
    ${attachmentFragment.attachment}
    ${attachmentGroupsFragment.attachmentGroup}
    ${fieldScoreFragment.fieldScore}
    ${unitPlanFragment.unitPlanBasicDetails}
    ${commonResolvedFieldValueFragment}
  `,
};

export const assignmentFragment = {
  assignmentFeed: gql`
    fragment assignmentItem on Assignment {
      id
      contentType
      state {
        state
        publishedAt
        updatedAt
      }
      createdAt
      isStudentSubmissionEnabled
      isTeacherEvaluationEnabled
      isClassDiscussionEnabled
      isSimilarityCheckEnabled
      isExcludedFromFinalGradeCalculation
      isPrivate
      isOnScreenCalculatorEnabled
      showStudentResponse
      isTimedTestEnabled
      isFixedStartTime
      isBackdateAssignment
      content {
        id
        ... on Assessment {
          id
          isWorksheetExtractionInProgress
          title {
            id
            value
          }
          academicTerm {
            id
            lockingStatus
          }
          assessmentType {
            id
            value
          }
          image {
            id
            value
          }
          fields(
            uids: [
              "duration"
              "studentTemplate"
              "ictDetails"
              "ictDetails_esas"
              "pp"
              "subjectLevel"
              "topic"
              "unitPlan"
            ]
          ) {
            id
            uid
            value
          }
          assessmentTools {
            edges {
              node {
                id
                assessmentToolItem {
                  id
                  ... on Analysis {
                    ...aiAnalysisItem
                  }
                }
                assessmentToolType
                isTeacherEvaluationEnable
                isStudentEvaluationEnable
              }
            }
          }
          isLearningGoalsEvaluationEnabled
          category {
            id
            label
          }
          assignments {
            totalCount
            userAssignmentsCount
          }
          isIncludedInCourseFlow
        }
      }
      deadline
      closeSubmissionDate
      visibility
      classDiscussion {
        id
        unreadMessageCount
        lastMessage {
          id
          isRead
          createdAt
        }
      }
      isSharedWithClassMaterial
      assignedStudents(studentIds: $studentIds) {
        id
        student {
          ...studentItem
        }
        conversation {
          id
          unreadMessageCount
          unreadMessageCounts(filters: $unreadMessageCountFilters) {
            itemTypeV2
            itemType
            count
          }
          lastMessage {
            id
            isRead
            createdAt
          }
        }
        submission {
          id
          status
          statusV2
        }
        state
        status
        isEvaluated
        isSharedWithStudent
        isEvaluationApplicable
        isSubmitted
        lockingState {
          state
        }
      }
      createdBy {
        ...staffItem
      }
      counts {
        totalAssignedStudents
        totalSubmissions
        totalDraftSubmissions
      }
      curriculumProgram {
        id
        type
      }
      taggedPractices {
        ...taggedPracticesOnEvidence
      }
      course {
        id
        title
        academicYears {
          id
        }
        subjects {
          id
          name
          type
        }
        primaryGrade {
          id
          name
        }
        learningCourse {
          id
        }
      }
      assignmentErrorMessages {
        itemId
        itemType
        errorCode
      }
    }
    ${studentDetailsFragment.basicStudentDetails}
    ${staffFragment.basicStaffDetails}
    ${taggedPracticesFragment.taggedPractices}
    ${assessmentToolFragment.aiAnalysis}
  `,
  assignmentReview: gql`
    fragment assignmentItem on Assignment {
      id
      contentType
      state {
        state
        publishedAt
        updatedAt
      }
      createdAt
      isStudentSubmissionEnabled
      isTeacherEvaluationEnabled
      isClassDiscussionEnabled
      deadline
      closeSubmissionDate
      visibility
      course {
        id
        title
      }
      content {
        id
        ... on Assessment {
          id
          title {
            id
            value
          }
          assessmentType {
            id
            value
          }
          assessmentTools {
            edges {
              node {
                id
                assessmentToolType
              }
            }
          }
          isLearningGoalsEvaluationEnabled
        }
        ... on AssignmentResource {
          id
          assignmentTitle: title
          label
          meeting {
            ...meetingDetailsItem
          }
        }
      }
      curriculumProgram {
        id
        type
      }
      classDiscussion {
        id
        unreadMessageCount
      }
      assignedStudents(studentIds: $studentIds) {
        id
        student {
          ...studentItem
        }
        conversation {
          id
          unreadMessageCount
          unreadMessageCounts(filters: $unreadMessageCountFilters) {
            itemTypeV2
            itemType
            count
          }
        }
        submission {
          id
          status
          statusV2
        }
        isEvaluated
        isSharedWithStudent
        isEvaluationApplicable
        isSubmitted
      }
      createdBy {
        ...staffItem
      }
      counts {
        totalAssignedStudents
        totalSubmissions
        totalDraftSubmissions
      }
    }
    ${studentFragment.basicStudentDetails}
    ${staffFragment.basicStaffDetails}
    ${meetingFragment.meetingDetails}
  `,
  assignmentEdit: gql`
    fragment assignmentItem on Assignment {
      id
      contentType
      isClassDiscussionEnabled
      isStudentSubmissionEnabled
      isTeacherEvaluationEnabled
      isStudentEvaluationEnabled
      isSimilarityCheckEnabled
      isPrivate
      isExcludedFromFinalGradeCalculation
      isOnScreenCalculatorEnabled
      showStudentResponse
      isBackdateAssignment
      folders {
        ...genericFolderItem
      }
      course {
        id
        primaryGrade {
          id
        }
      }
      content {
        id
        ... on Assessment {
          id
          assessmentTitle: title {
            id
            value
          }
          assessmentType {
            id
            value
          }
          image {
            id
            value
          }
          fields(
            uids: [
              "duration"
              "studentTemplate"
              "ictDetails"
              "ictDetails_esas"
              "pp"
              "subjectLevel"
              "topic"
              "unitPlan"
            ]
          ) {
            id
            uid
            value
          }
          assessmentTools {
            edges {
              node {
                id
                assessmentToolType
                isTeacherEvaluationEnable
                isStudentEvaluationEnable
              }
            }
          }
          isLearningGoalsEvaluationEnabled
          category {
            id
            label
          }
          globalCategories {
            id
            name
          }
          assignments @include(if: $isLinkedAssignments) {
            edges {
              id
              course {
                id
                title
                learningCourse {
                  id
                }
              }
              state {
                state
                publishedAt
                updatedAt
              }
              deadline
              closeSubmissionDate
              visibility
              assignedStudents {
                id
                isSubmitted
                student {
                  ...studentItem
                }
              }
              folders {
                ...genericFolderItem
              }
            }
          }
          isIncludedInCourseFlow
        }
        ... on AssignmentResource {
          id
          label
          title
          attachments {
            ...attachmentItem
          }
          studentTemplates {
            ...attachmentGroupItem
          }
          voiceInstruction {
            ...attachmentItem
          }
          meeting {
            ...meetingDetailsItem
          }
        }
      }
      state {
        state
        publishedAt
        updatedAt
      }
      createdAt
      deadline
      closeSubmissionDate
      visibility
      assignedStudents {
        id
        isSubmitted
        isEvaluated
        isSharedWithStudent
        state
        student {
          ...studentItem
        }
        submission {
          id
          status
          statusV2
          systemStatus
          overrideStatusUpdatedBy {
            id
            type
          }
          overrideStatusUpdatedAt
        }
      }
      isTimedTestEnabled
      isFixedStartTime
      isLockScreenEnabled
      isSafeBrowserEnabled
      timedTestDuration
      groupedAssignments {
        id
        course {
          id
          title
        }
        state {
          state
          publishedAt
          updatedAt
        }
        deadline
        closeSubmissionDate
        visibility
        assignedStudents {
          id
          isSubmitted
          student {
            ...studentItem
          }
        }
        folders {
          ...genericFolderItem
        }
      }
      isLockSubmissionEnabled
      isSharedWithClassMaterial
      metadata
    }
    ${genericFolderFragment.genericFolderBasicDetails}
    ${attachmentFragment.attachment}
    ${meetingFragment.meetingDetails}
    ${studentDetailsFragment.basicStudentDetails}
    ${attachmentGroupsFragment.attachmentGroup}
  `,
  assignmentDetails: gql`
    fragment assignmentItem on Assignment {
      id
      contentType
      isClassDiscussionEnabled
      isStudentSubmissionEnabled
      isTeacherEvaluationEnabled
      isStudentEvaluationEnabled
      isSimilarityCheckEnabled
      isExcludedFromFinalGradeCalculation
      isPrivate
      isOnScreenCalculatorEnabled
      showStudentResponse
      isLockSubmissionEnabled
      isBackdateAssignment
      publishedAt
      state {
        state
        publishedAt
      }
      metadata
      content {
        id
        ... on Assessment {
          id
          image {
            id
            value
          }
          academicTerm {
            id
            lockingStatus
          }
          title {
            id
            uid
            value
          }
          unitPlan {
            id
            templateId
          }
          aiUsageMetrics @include(if: $fetchAiUsageMetrics) {
            id
            type
          }
          isLearningGoalsEvaluationEnabled
          assessmentTools {
            edges {
              node {
                id
                assessmentToolItem {
                  id
                  ... on Analysis {
                    ...aiAnalysisItem
                  }
                  ... on StandardRubric {
                    ...standardRubricItem
                  }
                  ... on GradeScaleEntityDetail {
                    ...gradeScaleEntityDetailsItem
                  }
                  ... on Rubric {
                    ...rubricItem
                  }
                  ... on Checklist {
                    ...checklist
                  }
                  ... on SinglePointRubric {
                    ...singlePointRubricItem
                  }
                  ... on MypObjectiveRubric {
                    ...mypObjectiveRubricItem
                  }
                  ... on CommentAssessmentTool {
                    id
                  }
                  ... on ScoreAssessmentTool {
                    id
                    scoreToolData {
                      id
                      maxScore
                    }
                  }
                  ... on AcademicCriteriaSet {
                    id
                    academicCriteriaValues {
                      id
                      label
                      scoreValues {
                        id
                        value
                        minValue
                        maxValue
                        valueType
                        weightageType
                        genericTag {
                          id
                          label
                        }
                      }
                    }
                    criteriaType
                  }
                }
                assessmentToolType
                isTeacherEvaluationEnable
                isStudentEvaluationEnable
              }
            }
          }
          allFields {
            id
            uid
            value
            resolvedMinimalTree {
              ...resolvedMinimalTreeItem
            }
          }
          fields(uids: ["goals", "voiceInstruction", "studentTemplate"]) {
            id
            uid
            value
            resolvedMinimalTree {
              id
              ... on ResolvedFieldVoiceInstruction {
                id
                attachment {
                  ...attachmentItem
                }
              }

              ... on ResolvedFieldStudentTemplate {
                id
                attachmentGroups {
                  ...attachmentGroupItem
                }
              }
            }
          }
          organizationResources: organizationResourcesV2(
            orderBy: $organizationResourcesOrderBy
            orderByDirection: $organizationResourcesOrderByDirection
            first: $organizationResourcesFirst
            filters: $organizationResourceFilters
          ) {
            edges {
              node {
                ...organizationResourceItem
              }
            }
          }
          resources {
            totalCount
            edges {
              node {
                id
                attachments {
                  ...attachmentItem
                }
              }
            }
          }
          assessmentType {
            id
            value
          }
          taskType {
            id
            type
            label
          }
          templateId
          isLearningGoalsEvaluationEnabled
          category {
            id
            label
            colorCode
            set {
              id
            }
          }
          weightage
          aiConversation {
            edges {
              node {
                id
                entitySubType
                metadata
              }
            }
          }
          assignments {
            totalCount
            userAssignmentsCount
          }
          isIncludedInCourseFlow
          lockingInfo {
            isLocked
          }
          learningCourse {
            id
          }
        }
        ... on AssignmentResource {
          id
          label
          assignmentTitle: title
          voiceInstruction {
            ...attachmentItem
          }
          attachments {
            ...attachmentItem
          }
          studentTemplates {
            ...attachmentGroupItem
          }
        }
      }
      deadline
      closeSubmissionDate
      visibility
      classDiscussion {
        id
        unreadMessageCount
      }
      assignedStudents(studentIds: $studentIds) {
        id
        isSubmitted
        evaluatedAt
        studentResponsePostStatus
        student {
          ...studentItem
          ... on Student {
            id
            alertTypes {
              ...alertTypeFields
            }
          }
        }
        submission(showOnlySubmitted: $showOnlySubmitted) {
          ...studentAssigmentSubmissionItem
        }
        toolResponses: toolResponsesV2(
          filters: { userFilter: $userFilter, isFetchDeletedAiRatings: true }
        ) {
          id
          toolId
          toolType
          toolResponses {
            ...toolResponseItem
          }
        }
        ibPYPElementRatings(filters: { isFetchDeletedAiRatings: true }) {
          id
          value
          ibPYPElementId
          ibPYPElementType
          creatorType
          isDeleted
          academicCriteriaSet {
            id
            academicCriteriaValues {
              id
            }
          }
          academicCriteriaValue {
            id
            label
            abbreviation
          }
          evaluationJustification {
            id
            message
          }
        }
        elementRemarks(filters: { isFetchDeletedAiRatings: true }) {
          ...elementRemarkItem
        }
        conversation {
          id
          unreadMessageCount
          unreadMessageCounts(filters: $unreadMessageCountFilters) {
            itemTypeV2
            itemType
            count
          }
        }
        isEvaluated
        isSharedWithStudent
        isEvaluationApplicable
        aiEvaluationJob(filters: { isFetchLatestJob: true }) {
          id
          status
          jobStartExecutionTime
          metadata
        }
        status
        post {
          id
          state
        }
        events(filters: $entityEventsFilter) {
          ...entityEventTrackItem
        }
        lockingState {
          state
        }
        aiConversation {
          id
          metadata
          messages {
            totalCount
          }
          analysisToolEvaluation(filters: { userFilter: ALL }) {
            id
            toolResponses {
              ...toolResponseItem
            }
          }
        }
        aiConversations(orderBy: CREATED_AT, orderByDirection: DESC) {
          edges {
            node {
              id
              messages(filters: { isFlagged: true }) {
                totalCount
              }
            }
          }
        }
      }
      counts {
        totalAssignedStudents
        totalSubmissions
        totalDraftSubmissions
      }
      isDeleted
      curriculumProgram {
        id
        type
      }
      course {
        id
        grade {
          id
        }
        subjects {
          id
          name
          type
        }
        subjectGroups {
          id
          type
        }
        grades {
          id
        }
        primaryGrade {
          id
          name
        }
        genericTags {
          id
          label
        }
        learningCourse {
          id
        }
      }
      createdBy {
        id
        firstName
        middleName
        lastName
        profileImage
        type
        preferredName
        prefix
        suffix
        pronouns
      }
      isTimedTestEnabled
      isFixedStartTime
      isLockScreenEnabled
      isSafeBrowserEnabled
      timedTestDuration
      isFixedStartTime
      safeExamBrowserQuitPassword
      taggedPractices {
        ...taggedPracticesOnEvidence
      }
      isLockSubmissionEnabled
      toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
        id
        toolId
        toolType
        toolResponses {
          ...toolResponseItem
        }
      }
    }
    ${studentDetailsFragment.basicStudentDetails}
    ${rubricFragment.rubric}
    ${checklistFragment.checklist}
    ${singlePointRubricFragment.singlePointRubric}
    ${resolvedFieldValueFragment.resolvedMinimalTree}
    ${assessmentToolFragment.mypObjectiveRubric}
    ${attachmentFragment.attachment}
    ${attachmentGroupsFragment.attachmentGroup}
    ${organizationResourceFragment.organizationResource}
    ${toolResponseFragment.toolResponse}
    ${taggedPracticesFragment.taggedPractices}
    ${entityEventTrackFragment.entityEventTrack}
    ${studentAssigmentSubmissionFragment.studentAssigmentSubmission}
    ${elementRemarkFragment.elementRemark}
    ${assessmentToolFragment.aiAnalysis}
    ${toolResponseFragment.toolResponse}
    ${standardRubricFragment.standardRubric}
    ${gradeScaleToolFragment.gradeScaleEntityDetails}
    ${studentsAlertFragment}
  `,
  assignmentDeletedStatus: gql`
    fragment assignmentItem on Assignment {
      id
      isDeleted
    }
  `,
  assignmentInsights: gql`
    fragment assignmentItem on Assignment {
      id
      label
      createdAt
      deadline
      isStudentSubmissionEnabled
      contentType
      curriculumProgram {
        id
        type
      }
      content {
        id
        ... on Assessment {
          id
          title {
            id
            value
          }
          assessmentType {
            id
            value
          }
        }
        ... on AssignmentResource {
          id
          assignmentTitle: title
          label
        }
      }
    }
  `,
  assignmentTodos: gql`
    fragment assignmentItem on Assignment {
      id
      label
      createdAt
      deadline
      isStudentSubmissionEnabled
      isTeacherEvaluationEnabled
      isClassDiscussionEnabled
      isTimedTestEnabled
      isFixedStartTime
      contentType
      createdBy {
        id
        firstName
        lastName
        preferredName
        prefix
        suffix
        pronouns
        type
      }
      classDiscussion {
        id
        unreadMessageCount
      }
      course {
        id
      }
      publishedAt
      updatedAt
      state {
        state
        publishedAt
        updatedAt
      }
      closeSubmissionDate
      content {
        id
        ... on Assessment {
          id
          fields(
            uids: [
              "duration"
              "studentTemplate"
              "ictDetails"
              "ictDetails_esas"
              "pp"
              "subjectLevel"
              "topic"
            ]
          ) {
            id
            uid
            value
          }
          title {
            id
            value
          }
          image {
            id
            value
          }
          assessmentType {
            id
            value
          }
        }
        ... on AssignmentResource {
          id
          assignmentTitle: title
          label
          meeting {
            ...meetingDetailsItem
          }
        }
      }
      curriculumProgram {
        id
        type
      }
    }
    ${meetingFragment.meetingDetails}
  `,
  assignmentSelectionFeed: gql`
    fragment assignmentItem on Assignment {
      id
      contentType
      createdAt
      content {
        id
        ... on Assessment {
          id
          title {
            id
            value
          }
          image {
            id
            value
          }
          assessmentType {
            id
            value
          }
        }
        ... on AssignmentResource {
          id
          quickTaskTitle: title
        }
      }
    }
  `,
  assignmentEvalutionInsight: gql`
    fragment assignmentEvaluationItem on Assignment {
      id
      toolInsights {
        id
        toolId
        toolType
        insights {
          rowId
          columnId
          studentResponseCount
          teacherResponseCount
          studentsByTeacher {
            ...studentItem
          }
          studentsByStudent {
            ...studentItem
          }
        }
      }
    }
    ${studentDetailsFragment.basicStudentDetails}
  `,
  deletedAssignmentFeed: gql`
    fragment assignmentItem on Assignment {
      id
      contentType
      deletedBy {
        ... on Staff {
          id
          firstName
          middleName
          lastName
          preferredName
          prefix
          suffix
          pronouns
          isArchived
          isArchivedV2(filters: $userArchivedFilters)
        }
      }
      deletedAt
      content {
        id
        ... on Assessment {
          id
          title {
            id
            value
          }
          assessmentType {
            id
            value
          }
        }
      }
      curriculumProgram {
        id
        type
      }
    }
  `,
  assignmentFeedWithoutAssignedStudents: gql`
    fragment assignmentItem on Assignment {
      id
      contentType
      state {
        state
        publishedAt
        updatedAt
      }
      createdAt
      isStudentSubmissionEnabled
      isTeacherEvaluationEnabled
      isClassDiscussionEnabled
      isSimilarityCheckEnabled
      isExcludedFromFinalGradeCalculation
      isPrivate
      isOnScreenCalculatorEnabled
      isTimedTestEnabled
      isBackdateAssignment
      folders {
        id
        name
        color
      }
      isFixedStartTime
      content {
        id
        ... on Assessment {
          id
          isWorksheetExtractionInProgress
          title {
            id
            value
          }
          taskType {
            id
            type
            label
            color
            displaySequence
            metaData
          }

          academicTerm {
            id
            lockingStatus
          }
          assessmentType {
            id
            value
          }
          image {
            id
            value
          }
          globalCategories {
            id
            name
          }
          fields(
            uids: [
              "duration"
              "studentTemplate"
              "ictDetails"
              "ictDetails_esas"
              "pp"
              "subjectLevel"
              "topic"
              "unitPlan"
              "subjects"
            ]
          ) {
            id
            uid
            value
            resolvedMinimalTree {
              id
              ... on ResolvedFieldUnitPlan {
                id
                unitPlan {
                  id
                  title {
                    id
                    value
                  }
                  image {
                    id
                    resolvedMinimalTree {
                      id
                      ... on ResolvedFieldVideoAttachment {
                        id
                        attachment {
                          id
                          url
                        }
                      }
                    }
                  }
                }
              }
              ... on ResolvedFieldSubjectSet {
                id
                subjects {
                  id
                  name
                }
              }
            }
          }
          assessmentTools {
            edges {
              node {
                id
                assessmentToolItem {
                  id
                  ... on Analysis {
                    ...aiAnalysisItem
                  }
                }
                assessmentToolType
                isTeacherEvaluationEnable
                isStudentEvaluationEnable
              }
            }
          }
          isLearningGoalsEvaluationEnabled
          category {
            id
            label
          }
          assignments {
            totalCount
            edges {
              id
              course {
                id
                title
              }
            }
            userAssignmentsCount
          }
          isIncludedInCourseFlow
          learningCourse {
            id
            permissions {
              isManageAssignmentsAndResourcesInCourseFlow
            }
          }
          lockingInfo {
            isLocked
          }
        }
      }
      deadline
      closeSubmissionDate
      visibility
      classDiscussion {
        id
        unreadMessageCount
        lastMessage {
          id
          isRead
          createdAt
        }
      }
      isSharedWithClassMaterial
      createdBy {
        ...staffItem
      }
      counts {
        totalAssignedStudents
        totalSubmissions
        totalDraftSubmissions
      }
      curriculumProgram {
        id
        type
      }
      taggedPractices {
        ...taggedPracticesOnEvidence
      }
      course {
        id
        title
        academicYears {
          id
        }
        subjects {
          id
          name
          type
        }
        primaryGrade {
          id
          name
        }
        learningCourse {
          id
        }
      }
      assignmentErrorMessages {
        itemId
        itemType
        errorCode
      }
      ...assignmentSummaryCounts
      ...assignmentMessagesSummary
    }
    ${assignmentMessagesSummaryFragment.assignmentMessagesSummary}
    ${assignmentSummaryCountsFragment.assignmentSummaryCounts}
    ${staffFragment.basicStaffDetails}
    ${taggedPracticesFragment.taggedPractices}
    ${assessmentToolFragment.aiAnalysis}
  `,
};

export const courseAssignmentFragment = {
  courseAssignmentFeed: gql`
    fragment courseAssignmentItem on Course {
      id
      genericTags {
        id
        label
        type
      }
      assignments(
        first: $first
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: {
          createdBy: $createdBy
          contentTypes: $contentTypes
          searchText: $searchText
          states: $states
          folderIds: $folderIds
          showUpcoming: $showUpcoming
          assessmentTypes: $assessmentTypes
          excludedAssessmentTypes: $excludedAssessmentTypes
          isFetchUnreadConverstionAssignment: $isFetchUnreadConverstionAssignment
          isFetchToReviewAssigment: $isFetchToReviewAssigment
          academicTermIds: $academicTermIds
        }
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        durationSummary {
          slTotalDuration
          hlTotalDuration
        }
        edges {
          node {
            ...assignmentItem
          }
        }
      }
    }
    ${assignmentFragment.assignmentFeed}
  `,

  courseAssignmentFeedWithoutAssignedStudent: gql`
    fragment courseAssignmentItem on Course {
      id
      genericTags {
        id
        label
        type
      }
      assignments(
        first: $first
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: {
          createdBy: $createdBy
          contentTypes: $contentTypes
          searchText: $searchText
          states: $states
          folderIds: $folderIds
          showUpcoming: $showUpcoming
          assessmentTypes: $assessmentTypes
          excludedAssessmentTypes: $excludedAssessmentTypes
          isFetchUnreadConverstionAssignment: $isFetchUnreadConverstionAssignment
          isFetchToReviewAssigment: $isFetchToReviewAssigment
          isFetchToReviewAssigmentV2: $isFetchToReviewAssigmentV2
          academicTermIds: $academicTermIds
          createdByIds: $createdByIds
        }
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            ...assignmentItem
          }
        }
      }
    }
    ${assignmentFragment.assignmentFeedWithoutAssignedStudents}
  `,
  teacherReviewFilterCount: gql`
    fragment teacherReviewFilterCountItem on Organization {
      id
      LAST_WEEK: assignments(
        filters: {
          states: $states
          excludedAssessmentTypes: $excludedAssessmentTypes
          deadlineDates: {
            startDate: $lastWeekStartDate
            endDate: $lastWeekEndDate
          }
          archiveDates: {
            startDate: $archiveLastWeekStartDate
            endDate: $archiveLastWeekEndDate
          }
          contentTypes: $contentTypes
          courseIds: $courseIds
          createdBy: $createdBy
          searchText: $searchText
        }
      ) {
        totalCount
      }
      EARLIER: assignments(
        filters: {
          states: $states
          excludedAssessmentTypes: $excludedAssessmentTypes
          deadlineDates: { endDate: $earlierEndDate }
          archiveDates: { endDate: $archiveEarlierEndDate }
          contentTypes: $contentTypes
          courseIds: $courseIds
          createdBy: $createdBy
          searchText: $searchText
        }
      ) {
        totalCount
      }
      THIS_WEEK: assignments(
        filters: {
          states: $states
          excludedAssessmentTypes: $excludedAssessmentTypes
          deadlineDates: {
            startDate: $thisWeekStartDate
            endDate: $thisWeekEndDate
          }
          archiveDates: {
            startDate: $archiveThisWeekStartDate
            endDate: $archiveThisWeekEndDate
          }
          contentTypes: $contentTypes
          courseIds: $courseIds
          createdBy: $createdBy
          searchText: $searchText
        }
      ) {
        totalCount
      }
      NO_DUE_DATE: assignments(
        filters: {
          states: $states
          excludedAssessmentTypes: $excludedAssessmentTypes
          isNoDeadlineDate: true
          contentTypes: $contentTypes
          courseIds: $courseIds
          createdBy: $createdBy
          searchText: $searchText
        }
      ) {
        totalCount
      }
    }
  `,
  teacherClassroomReview: gql`
    fragment courseAssignmentItem on Organization {
      id
      assignments(
        first: 10
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: {
          createdBy: $createdBy
          contentTypes: $contentTypes
          searchText: $searchText
          courseIds: $courseIds
          states: $states
          showUpcoming: $showUpcoming
          deadlineDates: $deadlineDates
          archiveDates: $archiveDates
          isNoDeadlineDate: $isNoDeadlineDate
          excludedAssessmentTypes: $excludedAssessmentTypes
          isFetchUnreadConverstionAssignment: $isFetchUnreadConverstionAssignment
          isFetchToReviewAssigment: $isFetchToReviewAssigment
        }
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            ...assignmentItem
          }
        }
      }
    }
    ${assignmentFragment.assignmentReview}
  `,
  familyStudentTodos: gql`
    fragment familyStudentTodosItem on FamilyMember {
      id
      assignments(
        first: $first
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: {
          searchText: $searchText
          states: $states
          courseIds: $courseIds
          status: $status
          deadlineDates: $deadlineDates
          archiveDates: $archiveDates
          isNoDeadlineDate: $isNoDeadlineDate
          contentTypes: $contentTypes
          assessmentTypes: $assessmentTypes
          folderIds: $folderIds
          academicYears: $academicYears
          submissionStatus: $submissionStatus
          isFetchUnreadConverstionAssignment: $isFetchUnreadConverstionAssignment
          isEvalutationApplicable: $isEvaluationApplicable
        }
        studentIds: $studentIds
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            id
            isSubmitted
            isEvaluated
            status
            evaluatedAt
            state
            conversation {
              id
              unreadMessageCount
              unreadMessageCounts(filters: $unreadMessageCountFilters) {
                itemTypeV2
                count
              }
            }
            student {
              ...studentItem
            }
            submission {
              id
              status
              statusV2
              submittedAt
              returnedAt
              overrideStatusUpdatedAt
            }
            assignment {
              ...assignmentItem
            }
            assignmentState {
              publishedAt
              updatedAt
            }
            lockingState {
              state
            }
            isEvaluationApplicable
          }
        }
      }
    }
    ${assignmentFragment.assignmentTodos}
    ${studentFragment.basicStudentDetails}
  `,
  studentInsights: gql`
    fragment studentInsightsItem on Student {
      id
      assignments(
        first: 10
        after: $after
        orderBy: PUBLISHED_AT
        orderByDirection: DESC
        filters: {
          contentTypes: $contentTypes
          searchText: $searchText
          states: $states
          folderIds: $folderIds
          courseIds: [$courseIds]
          status: $status
          assessmentTypes: $assessmentTypes
          excludedAssessmentTypes: $excludedAssessmentTypes
        }
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            id
            isSubmitted
            isEvaluated
            evaluatedAt
            status
            student {
              ...studentItem
            }
            submission {
              id
              status
              submittedAt
              returnedAt
            }

            assignment {
              ...assignmentItem
            }
          }
        }
      }
    }
    ${assignmentFragment.assignmentInsights}
    ${studentFragment.basicStudentDetails}
  `,
  studentTodos: gql`
    fragment studentTodosItem on Student {
      id
      assignments(
        first: $first
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: {
          contentTypes: $contentTypes
          assessmentTypes: $assessmentTypes
          folderIds: $folderIds
          searchText: $searchText
          states: $states
          courseIds: $courseIds
          status: $status
          deadlineDates: $deadlineDates
          archiveDates: $archiveDates
          isNoDeadlineDate: $isNoDeadlineDate
          academicYears: $academicYears
          courseArchivalState: ALL
          excludedAssessmentTypes: $excludedAssessmentTypes
          submissionStatus: $submissionStatus
          isFetchUnreadConverstionAssignment: $isFetchUnreadConverstionAssignment
          isEvalutationApplicable: $isEvaluationApplicable
        }
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            id
            isSubmitted
            isEvaluated
            status
            evaluatedAt
            state
            conversation {
              id
              unreadMessageCount
              unreadMessageCounts(filters: $unreadMessageCountFilters) {
                itemTypeV2
                count
              }
            }
            student {
              ...studentItem
            }
            submission {
              id
              status
              statusV2
              submittedAt
              returnedAt
              overrideStatusUpdatedAt
            }
            assignment {
              ...assignmentItem
            }
            assignmentState {
              publishedAt
              updatedAt
            }
            lockingState {
              state
            }
            isEvaluationApplicable
          }
        }
      }
    }
    ${assignmentFragment.assignmentTodos}
    ${studentFragment.basicStudentDetails}
  `,
  studentTodosFilterCount: gql`
    fragment studentTodosFilterCountItem on Student {
      id
      LAST_WEEK: assignments(
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          deadlineDates: {
            startDate: $lastWeekStartDate
            endDate: $lastWeekEndDate
          }
          archiveDates: {
            startDate: $archiveLastWeekStartDate
            endDate: $archiveLastWeekEndDate
          }
        }
      ) {
        totalCount
      }
      EARLIER: assignments(
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          deadlineDates: { endDate: $earlierEndDate }
          archiveDates: { endDate: $archiveEarlierEndDate }
        }
      ) {
        totalCount
      }
      THIS_WEEK: assignments(
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          deadlineDates: {
            startDate: $thisWeekStartDate
            endDate: $thisWeekEndDate
          }
          archiveDates: {
            startDate: $archiveThisWeekStartDate
            endDate: $archiveThisWeekEndDate
          }
        }
      ) {
        totalCount
      }
      LATER: assignments(
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          deadlineDates: { startDate: $laterStartDate }
          archiveDates: { startDate: $archiveLaterStartDate }
        }
      ) {
        totalCount
      }
      NO_DUE_DATE: assignments(
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          isNoDeadlineDate: true
        }
      ) {
        totalCount
      }
    }
  `,
  familyStudentTodosFilterCount: gql`
    fragment familyStudentTodosFilterCountItem on FamilyMember {
      id
      LAST_WEEK: assignments(
        studentIds: $studentIds
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          deadlineDates: {
            startDate: $lastWeekStartDate
            endDate: $lastWeekEndDate
          }
          archiveDates: {
            startDate: $archiveLastWeekStartDate
            endDate: $archiveLastWeekEndDate
          }
        }
      ) {
        totalCount
      }
      EARLIER: assignments(
        studentIds: $studentIds
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          deadlineDates: { endDate: $earlierEndDate }
          archiveDates: { endDate: $archiveEarlierEndDate }
        }
      ) {
        totalCount
      }
      THIS_WEEK: assignments(
        studentIds: $studentIds
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          deadlineDates: {
            startDate: $thisWeekStartDate
            endDate: $thisWeekEndDate
          }
          archiveDates: {
            startDate: $archiveThisWeekStartDate
            endDate: $archiveThisWeekEndDate
          }
        }
      ) {
        totalCount
      }
      LATER: assignments(
        studentIds: $studentIds
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          deadlineDates: { startDate: $laterStartDate }
          archiveDates: { startDate: $archiveLaterStartDate }
        }
      ) {
        totalCount
      }
      NO_DUE_DATE: assignments(
        studentIds: $studentIds
        filters: {
          academicYears: $academicYears
          states: $states
          status: $status
          courseIds: $courseIds
          contentTypes: $contentTypes
          isNoDeadlineDate: true
        }
      ) {
        totalCount
      }
    }
  `,
  courseAssignmentStateWiseCount: gql`
    fragment courseAssignmentItem on Course {
      id
      assignments(
        filters: { excludedAssessmentTypes: $excludedAssessmentTypes }
      ) {
        stateWiseCount {
          state
          count
        }
      }
    }
  `,
  courseAssignmentStateWiseCountV2: gql`
    fragment courseAssignmentItem on Course {
      id
      assignments(filters: $filters) {
        stateWiseCountV2 {
          toReview
          reviewed
          unreadMessageAssignment
          scheduled
          draft
          all
        }
      }
    }
  `,
  courseDeletedAssignmentFeed: gql`
    fragment courseAssignmentItem on Course {
      id
      assignments(
        first: $first
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: {
          isFetchDeletedAssignments: $isFetchDeletedAssignments
          excludedAssessmentTypes: $excludedAssessmentTypes
        }
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            ...assignmentItem
          }
        }
      }
    }
    ${assignmentFragment.deletedAssignmentFeed}
  `,
};

export const organizationAssignmentFragment = {
  organizationAssignmentFeed: gql`
    fragment organizationAssignmentItem on Organization {
      assignments(
        first: $first
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: $filters
      ) {
        stateWiseCount {
          state
          count
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            ...assignmentItem
          }
        }
        totalCount
      }
    }
    ${assignmentFragment.assignmentSelectionFeed}
  `,
  classStreamManagerFeed: gql`
    fragment organizationAssignmentItem on Organization {
      id
      assignments(
        first: 25
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: {
          createdBy: $createdBy
          contentTypes: $contentTypes
          searchText: $searchText
          courseIds: $courseIds
          states: $states
          showUpcoming: $showUpcoming
          deadlineDates: $deadlineDates
          archiveDates: $archiveDates
          isNoDeadlineDate: $isNoDeadlineDate
          excludedAssessmentTypes: $excludedAssessmentTypes
          isFetchUnreadConverstionAssignment: $isFetchUnreadConverstionAssignment
          isFetchToReviewAssigment: $isFetchToReviewAssigment
          academicTermIds: $academicTermIds
        }
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            ...assignmentItem
          }
        }
      }
    }
    ${assignmentFragment.assignmentFeed}
  `,
  organizationAssignmentStateWiseCountV2: gql`
    fragment organizationAssignmentItem on Organization {
      id
      assignments(filters: $filters) {
        stateWiseCountV2 {
          toReview
          reviewed
          unreadMessageAssignment
          scheduled
          draft
          all
        }
      }
    }
  `,
};

export const courseUserMapFragment = {
  courseUserMap: gql`
    fragment courseItem on Course {
      id
      userMap(filters: $filters) {
        edges {
          node {
            id
            user {
              id
              ... on Student {
                ...studentItem
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
            isRemovedFromClass
            tags {
              id
              ...basicGenericTagItem
            }
          }
        }
      }
    }
    ${studentDetailsFragment.basicStudentDetails}
    ${genericTagFragment.genericTag}
    ${studentsAlertFragment}
  `,
};

export const tasksStateWiseCountFragment = {
  studentCountItem: gql`
    fragment studentCountItem on Student {
      id
      tasks(
        filters: {
          academicYearId: $academicYearId
          courseIds: $courseIds
          entityTypes: $entityTypes
          excludedAssessmentTypes: $excludedAssessmentTypes
          startDate: $startDate
          endDate: $endDate
          projectGroupIds: $projectGroupIds
          searchText: $searchText
          overDueStartDate: $overDueStartDate
          overDueEndDate: $overDueEndDate
          curriculumProgramIds: $curriculumProgramIds
        }
      ) {
        statusWiseCount {
          category
          count
          __typename
        }
        __typename
      }
      __typename
    }
  `,

  parentCountItem: gql`
    fragment parentCountItem on FamilyMember {
      id
      tasks(
        filters: {
          academicYearId: $academicYearId
          courseIds: $courseIds
          entityTypes: $entityTypes
          excludedAssessmentTypes: $excludedAssessmentTypes
          startDate: $startDate
          endDate: $endDate
          projectGroupIds: $projectGroupIds
          searchText: $searchText
          overDueStartDate: $overDueStartDate
          overDueEndDate: $overDueEndDate
          curriculumProgramIds: $curriculumProgramIds
        }
        studentIds: $studentIds
      ) {
        statusWiseCount {
          category
          count
          __typename
        }
        __typename
      }
      __typename
    }
  `,
};

export const standardSetRatingFragment = {
  standardSetForRating: gql`
    fragment standardSetForRatingItem on PlannerElementNodeSet {
      id
      title
      subject {
        id
      }
      gradeScales {
        id
        displayName
        gradeScaleEntityDetails(filters: $gradeScaleEntityDetailsFilters) {
          id
          entityDetail {
            learningCourseId
            curriculumProgramId
            academicTermId
          }
          gradeScaleValues {
            id
            label
            abbreviation
            displaySequence
            description
            color
            gradeScaleValueScores {
              id
              minValue
              maxValue
            }
          }
        }
      }
      standardSetWrapper {
        publishedStandards(filters: $plannerElementNodeFilters)
          @skip(if: $isPYPCurriculum) {
          edges {
            node {
              id
              parent
              gradingType
              standardsWrapper {
                properties {
                  isGradedStandard
                }
                standardSet {
                  id
                }
              }
            }
          }
        }
        publishedBenchmarks(filters: $plannerElementNodeFilters)
          @include(if: $isPYPCurriculum) {
          edges {
            node {
              id
              parent
              gradingType
              standardsWrapper {
                properties {
                  isGradedStandard
                }
                standardSet {
                  id
                }
              }
            }
          }
        }
      }
    }
  `,
};
